import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PayrollNavbar, { NavbarItemProps } from '../../../components/ProfileInnerNavbar';
import { useTranslation } from 'react-i18next';
import { Roles } from '@/components/roles';
import { useComplianceCountry } from '../../../hooks/useComplianceCountry';
import type { RootState } from '../../../store/redux/store/store';

export default ({ children }: any) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const complianceCountry = useComplianceCountry();
  const loggedInUserId = useSelector((state: RootState) => state.user.userId);
  const selectedUser = useSelector((state: RootState) => state.user.selectedUser);
  const isOwnProfile = id === loggedInUserId;
  const isSaudiEmployee = !!selectedUser?.saudiNationalIdNumber;
  const showBenefitsForEmployee = isOwnProfile && complianceCountry === 'KSA' && !isSaudiEmployee;
  // EOS + Final Settlement are HR-owned tools (Admin is NOT HR — see CLAUDE.md Role Model). An employee
  // — including an Admin, who is also an employee — may read their OWN record (self-service). Route guard
  // + `isHr` inside each page keep viewing-others and all mutations HR-only.
  const showEosForEmployee = isOwnProfile;

  // The HR tab set is wider than the strip: keep the ACTIVE sub-tab in view (it sat off-screen past
  // "Final settlement") and fade the end edge while more tabs are hidden, so the overflow is visible.
  const location = useLocation();
  const stripRef = useRef<HTMLDivElement>(null);
  const [showEndFade, setShowEndFade] = useState(false);
  useEffect(() => {
    const root = stripRef.current;
    if (!root) return undefined;
    const scroller = () => root.querySelector<HTMLElement>('nav[role="tablist"] > div');
    const updateFade = () => {
      const el = scroller();
      if (!el) { setShowEndFade(false); return; }
      const max = el.scrollWidth - el.clientWidth;
      // scrollLeft runs 0..max in LTR and 0..-max in RTL; the distance travelled is the same either way.
      setShowEndFade(max > 1 && Math.abs(el.scrollLeft) < max - 1);
    };
    const revealActive = () => {
      const el = scroller();
      const active = el?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      if (el && active) {
        const box = el.getBoundingClientRect();
        const tab = active.getBoundingClientRect();
        const pad = 48; // clear the fade
        if (tab.right > box.right - pad) el.scrollBy({ left: tab.right - box.right + pad });
        else if (tab.left < box.left + pad) el.scrollBy({ left: tab.left - box.left - pad });
      }
      updateFade();
    };
    // The navbar marks its active tab in its own effect, one render after this one: wait two frames.
    let frame = requestAnimationFrame(() => { frame = requestAnimationFrame(revealActive); });
    root.addEventListener('scroll', updateFade, true);
    window.addEventListener('resize', updateFade);
    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener('scroll', updateFade, true);
      window.removeEventListener('resize', updateFade);
    };
  }, [location.pathname]);

  const navbarItems: NavbarItemProps[] = [
    { id: 'payrollmonth', content: t('ACTIVE PAYROLL MONTH'), to: `/profile/${id}/payroll/active-payroll-month`, roles: Roles.CLOCKING },
    { id: 'salaryConfiguration', content: t('SALARY CONFIGURATION'), to: `/profile/${id}/payroll/salary-configuration`, roles: Roles.HR },
    // Hourly pay (docs/hourly-pay-spec.md §3.1/§3.2): pay basis + rate history. Salary data, so HR
    // only — including on the employee's own profile (the route is HR_ONLY_ROLES in App.tsx).
    { id: 'hourlyPay', content: t('HOURLY PAY'), to: `/profile/${id}/payroll/hourly-pay`, roles: Roles.HR },
    { id: 'variablePay', content: t('VARIABLE PAY'), to: `/profile/${id}/payroll/variable-pay`, roles: Roles.HR },
    { id: 'adjustments', content: t('ADJUSTMENTS'), to: `/profile/${id}/payroll/adjustments`, roles: Roles.HR },
    { id: 'benefits', content: t('BENEFITS'), to: `/profile/${id}/payroll/benefits`, roles: showBenefitsForEmployee ? Roles.CLOCKING : Roles.HR },
    { id: 'payHistory', content: t('PAY HISTORY'), to: `/profile/${id}/payroll/pay-history`, roles: Roles.CLOCKING },
    { id: 'endOfService', content: t('END OF SERVICE'), to: `/profile/${id}/payroll/end-of-service`, roles: showEosForEmployee ? Roles.CLOCKING : Roles.HR },
    { id: 'finalSettlement', content: t('FINAL SETTLEMENT'), to: `/profile/${id}/payroll/final-settlement`, roles: showEosForEmployee ? Roles.CLOCKING : Roles.HR },
    { id: 'payslips', content: t('PAYSLIPS'), to: `/profile/${id}/payroll/payslips`, roles: Roles.CLOCKING },
  ];

  return (
    <div className="mt-4">
      <div ref={stripRef} className="relative">
        <PayrollNavbar items={navbarItems} primaryColor={"var(--mowazaf-primary)"} />
        {showEndFade && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-0 bottom-px end-0 w-12 rounded-e-lg bg-gradient-to-l rtl:bg-gradient-to-r from-card to-transparent"
          />
        )}
      </div>
      <div className="mt-4 p-4">
        {children}
      </div>
    </div>
  );
};

